import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plugs, CreditCard, Truck, ShoppingBag, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

const ADAPTERS = [
  { key: 'stripe', label: 'Stripe', icon: CreditCard, color: '#635bff', desc: 'Card payments (mock)' },
  { key: 'doordash', label: 'DoorDash', icon: Truck, color: '#ee2737', desc: 'Delivery orders (mock)' },
  { key: 'ubereats', label: 'Uber Eats', icon: ShoppingBag, color: '#06c167', desc: 'Delivery orders (mock)' },
];

export const IntegrationsPanel = ({ outletId }) => {
  const [status, setStatus] = useState({});
  const [busy, setBusy] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/integrations/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const fireStripe = async () => {
    setBusy('stripe');
    try {
      const r = await axios.post(`${API_URL}/api/integrations/stripe/charge`,
        { amount: 9.99, outlet_id: outletId }, { withCredentials: true });
      toast.success(`Stripe charge OK · ${r.data.transaction_id.slice(0, 14)}…`);
    } catch { toast.error('Stripe mock failed'); }
    setBusy(null);
  };

  const fireDelivery = async (provider) => {
    setBusy(provider);
    try {
      const r = await axios.post(`${API_URL}/api/integrations/${provider}/webhook`,
        { outlet_id: outletId, customer_name: `${provider} customer`, external_id: `EXT-${Date.now()}` },
        { withCredentials: true });
      toast.success(`${provider} incoming order ${r.data.order_number} created`);
    } catch { toast.error(`${provider} mock failed`); }
    setBusy(null);
  };

  return (
    <Card className="border border-[var(--theme-border)] bg-[var(--theme-surface)]" data-testid="integrations-panel">
      <CardHeader className="border-b border-[var(--theme-border)]">
        <CardTitle className="font-heading text-lg flex items-center gap-2 text-[var(--theme-text)]">
          <Plugs size={20} className="text-[var(--theme-accent)]" />
          3rd-Party Integrations
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {ADAPTERS.map((a) => {
          const Icon = a.icon;
          const s = status[a.key];
          return (
            <div
              key={a.key}
              className="rounded-xl border border-[var(--theme-border)] p-3 flex flex-col gap-3"
              data-testid={`integration-${a.key}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${a.color}20`, color: a.color }}
                >
                  <Icon size={20} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[var(--theme-text)]">{a.label}</p>
                  <p className="text-xs text-[var(--theme-text-muted)]">{a.desc}</p>
                </div>
                {s?.connected && (
                  <CheckCircle size={16} weight="fill" className="ml-auto text-green-500" />
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => (a.key === 'stripe' ? fireStripe() : fireDelivery(a.key))}
                disabled={busy === a.key}
                data-testid={`integration-test-${a.key}`}
              >
                <PaperPlaneTilt size={14} className="mr-1" />
                {a.key === 'stripe' ? 'Test charge $9.99' : 'Simulate incoming order'}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default IntegrationsPanel;
